import React, { useEffect, useState } from 'react';
import { Button, Modal, Spin } from 'antd';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { type AppDispatch } from '../../redux/store';
import {
  showErrorMessage,
  showSuccessMessage,
} from '../../components/ToastComponent/store/toastMiddleware';
import OtpInput from '../SignUp/OtpInput';
import { formatTime } from '../SignUp/authUtils';
import {
  sendLedgerEmailOtp,
  verifyLedgerEmail,
  type LedgerEmailEntry,
  type LedgerEmailOtpResponse,
} from './ledgerEmailsApi';

const OTP_LENGTH = 6;
const GENERIC_MESSAGE = 'Something went wrong. Please try again.';

interface LedgerEmailOtpDialogProps {
  open: boolean;
  // the address being added (or the new address when editing); the OTP is sent to it
  email: string;
  // set when editing: the address that will be replaced
  replaceEmail?: string;
  // the response of the first send-otp call (masked address and timers)
  sent: LedgerEmailOtpResponse | null;
  // closed without saving; `message` is an error the page should show under its input
  onClose: (message?: string) => void;
  // verified: the list to show
  onDone: (emails: LedgerEmailEntry[]) => void;
}

// Verify an email with the OTP that was sent to it. Nothing is saved until verify succeeds.
export default function LedgerEmailOtpDialog({
  open,
  email,
  replaceEmail,
  sent,
  onClose,
  onDone,
}: LedgerEmailOtpDialogProps): JSX.Element {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const [maskedEmail, setMaskedEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [expiresLeft, setExpiresLeft] = useState(0);
  const [resendLeft, setResendLeft] = useState(0);
  const [isBusy, setIsBusy] = useState(false);

  const startTimers = (res: LedgerEmailOtpResponse) => {
    setMaskedEmail(res.email);
    setExpiresLeft(res.expiresInSeconds);
    setResendLeft(res.resendAfterSeconds);
    setOtp('');
    setOtpError('');
  };

  // Start from the OTP that was just sent each time the dialog opens
  useEffect(() => {
    if (open && sent !== null) startTimers(sent);
    // eslint-disable-next-line
  }, [open, sent]);

  // Expiry + resend countdowns
  useEffect(() => {
    if (expiresLeft <= 0 && resendLeft <= 0) return;
    const timer = window.setTimeout(() => {
      setExpiresLeft((s) => Math.max(s - 1, 0));
      setResendLeft((s) => Math.max(s - 1, 0));
    }, 1000);
    return () => {
      window.clearTimeout(timer);
    };
  }, [expiresLeft, resendLeft]);

  const isExpired = expiresLeft <= 0;
  const canResend = resendLeft <= 0 || isExpired;

  const messageOf = (data: any): string =>
    typeof data?.message === 'string' ? data.message : GENERIC_MESSAGE;

  const handleAuthError = (status: number | undefined): boolean => {
    if (status === 401) {
      window.localStorage.clear();
      navigate('/sign-in');
      return true;
    }
    return false;
  };

  const onResend = async () => {
    if (!canResend || isBusy) return;
    setIsBusy(true);
    try {
      const res = await sendLedgerEmailOtp({ email, replaceEmail });
      startTimers(res);
      dispatch(showSuccessMessage('A new OTP has been sent'));
    } catch (error: any) {
      const status: number | undefined = error?.response?.status;
      if (handleAuthError(status)) return;
      // 429 "Please wait N seconds..." keeps the resend timer running; 500 can be retried
      dispatch(showErrorMessage(messageOf(error?.response?.data)));
    } finally {
      setIsBusy(false);
    }
  };

  const onVerify = async () => {
    if (isBusy) return;
    if (otp.length < OTP_LENGTH) {
      setOtpError(`Enter the ${OTP_LENGTH}-digit code`);
      return;
    }
    setIsBusy(true);
    try {
      const res = await verifyLedgerEmail({ email, otp });
      dispatch(
        showSuccessMessage(
          res?.message ??
            (replaceEmail !== undefined ? 'Email updated' : 'Email added')
        )
      );
      onDone(res.emails);
    } catch (error: any) {
      const status: number | undefined = error?.response?.status;
      const data = error?.response?.data;
      const message = messageOf(data);
      if (handleAuthError(status)) return;
      if (data?.field === 'otp') {
        if (status === 429) {
          // too many wrong attempts: the OTP is discarded, start again
          dispatch(showErrorMessage(message));
          onClose();
          return;
        }
        setOtp('');
        setOtpError(message);
        if (/expired/i.test(message)) setExpiresLeft(0);
      } else if (data?.field === 'email' || data?.field === 'newEmail') {
        // e.g. the address was taken meanwhile: back to the input with the message
        onClose(message);
      } else {
        dispatch(showErrorMessage(message));
      }
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      title="Verify the email"
      footer={null}
      onCancel={() => {
        if (!isBusy) onClose();
      }}
      maskClosable={false}
      destroyOnClose
      centered
      getContainer={false}
      width={520}
      className="settings__modal"
    >
      <Spin spinning={isBusy} fullscreen />
      <p className="ledger-emails__dialog-text">
        We sent a {OTP_LENGTH}-digit code to <strong>{maskedEmail}</strong>.
        Enter it below to {replaceEmail !== undefined ? 'update' : 'add'} this
        email. Nothing is saved until the code is verified.
      </p>
      <OtpInput
        value={otp}
        onChange={(value) => {
          setOtp(value);
          setOtpError('');
        }}
        length={OTP_LENGTH}
        hasError={otpError !== ''}
      />
      <p className="ledger-emails__otp-error">{otpError}</p>
      <p className="ledger-emails__dialog-text ledger-emails__dialog-text--center">
        {isExpired ? (
          <span className="ledger-emails__expired">OTP expired</span>
        ) : (
          <>Code expires in {formatTime(expiresLeft)}</>
        )}
        {' · '}
        {canResend ? (
          <span
            className="ledger-emails__link"
            role="button"
            tabIndex={0}
            onClick={() => {
              void onResend();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void onResend();
            }}
          >
            Resend code
          </span>
        ) : (
          <>Resend in {formatTime(resendLeft)}</>
        )}
      </p>
      <div className="settings__actions">
        <Button
          onClick={() => {
            onClose();
          }}
          disabled={isBusy}
          className="settings__button settings__button--ghost"
        >
          Cancel
        </Button>
        <Button
          onClick={() => {
            void onVerify();
          }}
          disabled={otp.length < OTP_LENGTH || isExpired || isBusy}
          className="settings__button settings__button--primary"
        >
          Verify and {replaceEmail !== undefined ? 'update' : 'add'}
        </Button>
      </div>
    </Modal>
  );
}
